import React, { useState, useEffect, useMemo } from 'react';
import { Container, Typography, Box, Paper, Divider, Button, Grid, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useEventEdition } from '../contexts/EventEditionContext';
import { getKrultraUrl } from '../config/urls';
import { useNavigate } from 'react-router-dom';
import { getEventLogoUrl } from '../services/strapiService';

function formatCountdown(target: Date) {
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return '0 dager 0 timer 0 minutter 0 sekunder';
  const dager = Math.floor(diff / (1000 * 60 * 60 * 24));
  const timer = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutter = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const sekunder = Math.floor((diff % (1000 * 60)) / 1000);
  return `${dager} dager ${timer} timer ${minutter} minutter ${sekunder} sekunder`;
}

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  return null;
}

const MO2025Page: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { event, loading, error, setEvent } = useEventEdition();

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  
  // Load the MO-2025 event when the component mounts
  useEffect(() => {
    setEvent('mo-2025');
  }, [setEvent]);

  useEffect(() => {
    let isMounted = true;
    const loadLogo = async () => {
      try {
        const url = await getEventLogoUrl(String((event as any)?.eventId || 'mo'));
        if (isMounted) setLogoUrl(url);
      } catch {
        if (isMounted) setLogoUrl(null);
      }
    };
    loadLogo();
    return () => {
      isMounted = false;
    };
  }, [event]);

  // Extract dates from event
  const raceDate = useMemo(() => toDate(event?.startTime) || new Date(), [event?.startTime]);
  const registrationDeadline = useMemo(() => toDate(event?.registrationDeadline), [event?.registrationDeadline]);
  
  const [nedtelling, setNedtelling] = useState<string>(formatCountdown(raceDate));

  useEffect(() => {
    const timer = setInterval(() => setNedtelling(formatCountdown(raceDate)), 1000);
    return () => clearInterval(timer);
  }, [raceDate]);

  // Determine if event is in the past
  const now = new Date();
  const isPastEvent = raceDate < now;
  const isRegistrationOpen = registrationDeadline ? now < registrationDeadline : false;
  const liveResultsURL = event?.liveResultsURL ?? '';
  const isEventOngoing = event?.resultsStatus === 'ongoing';
  const showLiveResultsButton = Boolean(isEventOngoing && liveResultsURL);

  let statusMessage = '';
  if (isEventOngoing) {
    statusMessage = 'Løpet pågår nå';
  } else if (isPastEvent) {
    statusMessage = 'Løpet er avsluttet';
  } else if (!isRegistrationOpen) {
    statusMessage = 'Påmeldingen er stengt';
  } else {
    statusMessage = 'Påmeldingen er åpen';
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{t('events.errorLoadingEvent', { message: error.message })}</Typography>
      </Box>
    );
  }

  if (!event) {
    return (
      <Box p={3}>
        <Alert severity="warning">{t('events.noEventData')}</Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt="MO logo"
              sx={{ width: 44, height: 44, borderRadius: 1, objectFit: 'cover' }}
            />
          )}
          <Typography variant="h2" component="h1" gutterBottom sx={{ mb: 0 }}>
            {event.eventName || 'Malvikingen Opp 2025'}
          </Typography>
        </Box>
        <Typography variant="h5" color="text.secondary" paragraph>
          Malviks eldste motbakkeløp
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Button
            component="a"
            href={getKrultraUrl('events/MO')}
            variant="text"
            color="inherit"
            sx={{ fontWeight: 400, px: 1, py: 0.5, minWidth: 0, fontSize: '1rem', textTransform: 'none', textDecoration: 'underline', textUnderlineOffset: 4 }}
          >
            {t('mo.moreInfoLink')}
          </Button>
          <Button
            component="a"
            href="https://www.facebook.com/groups/146973852042384/"
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            color="inherit"
            sx={{ fontWeight: 400, px: 1, py: 0.5, minWidth: 0, fontSize: '0.95rem', textTransform: 'none', textDecoration: 'underline', textUnderlineOffset: 4 }}
          >
            {t('mo.facebookGroupNote')}
          </Button>
        </Box>
        <Paper elevation={1} sx={{ borderRadius: 2, p: 3, mb: 4 }}>
          <Typography variant="h4">
            {raceDate.toLocaleDateString(undefined, {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
          </Typography>
          <Typography variant="h6">
            Start konkurranseklasser: {raceDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            <br />
            Start turklasse: {t('mo.startTurklasse')}
          </Typography>
          {!isPastEvent && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5">
                {nedtelling} igjen
              </Typography>
            </Box>
          )}
        </Paper>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2, mt: 2, mb: 2 }}>
          {showLiveResultsButton && liveResultsURL && (
            <Button
              component="a"
              variant="contained"
              color="secondary"
              size="large"
              href={liveResultsURL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 700, minWidth: 220 }}
            >
              Se live resultater
            </Button>
          )}
          {!isPastEvent && !isEventOngoing && isRegistrationOpen && (
            <>
              <Button
                component="a"
                variant="contained"
                color="primary"
                size="large"
                href="https://docs.google.com/forms/d/e/1FAIpQLSfUjLiF7JwKMiKA8kpplwlGjmxgGZ1slE_IWNmxZucqHSj95g/viewform?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 700, minWidth: 220 }}
              >
                {t('mo.hikingClassRegistration')}
              </Button>
              <Button
                component="a"
                variant="contained"
                color="primary"
                size="large"
                href="https://signup.eqtiming.com/?Event=Malvik_IL&lang=norwegian"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 700, minWidth: 220 }}
              >
                {t('mo.competitionRegistration')}
              </Button>
            </>
          )}
          <Button
            component="a"
            variant="outlined"
            color="inherit"
            href="https://signup.eqtiming.com/participants?Event=malvik_il&uid=76727"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 700, minWidth: 220 }}
          >
            {t('events.seeParticipants')}
          </Button>
          {isPastEvent && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/mo/results')}
              sx={{ fontWeight: 700, minWidth: 220 }}
            >
              {t('events.seeResults')}
            </Button>
          )}
        </Box>

        {statusMessage && (
          <Alert severity={isEventOngoing ? 'success' : 'info'} sx={{ mb: 4 }}>
            {statusMessage}
          </Alert>
        )}

        <Box sx={{ flexGrow: 1, mb: 4 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>{t('mo.aboutTitle')}</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography paragraph>
                  {t('mo.aboutText')}
                </Typography>
                <Typography paragraph>
                  Malvikingen Opp så dagens lys i 2011 og er det eldste motbakkeløpet i Malvik. Arrangør er Malvik IL Friidrett.
                </Typography>
                <Typography><b>Løpsdato:</b> Lørdag 10. mai 2025</Typography>
                <Typography><b>Start:</b> 10:00–12:00 for tur, 12:00 for konkurranse og trim m/tidtaking</Typography>
                <Typography><b>Start:</b> Vikhammerløkka</Typography>
                <Typography><b>Mål:</b> Solemsvåttan</Typography>
                <Typography><b>Lengde:</b> 6 km</Typography>
                <Typography><b>Høydemeter:</b> Start 3 moh, mål 423 moh.</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Påmelding og betaling</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography><b>Påmeldingsfrist:</b> {registrationDeadline?.toLocaleDateString('nb-NO') || 'Ikke satt'}</Typography>
                <Typography><b>Deltakeravgift:</b></Typography>
                <ul style={{ textAlign: 'left', marginTop: 4, marginBottom: 8 }}>
                  <li>200,- kroner for konkurranseklasser og trim med tidtaking <br /><span style={{ fontSize: '0.95em', color: '#555' }}>(+ evt. engangslisens 30,- for de som ikke har årslisens)</span></li>
                  <li>50,- kroner for tur/mosjonsklasse <span style={{ fontSize: '0.95em', color: '#555' }}>(ingen lisenskrav)</span></li>
                </ul>
                <Typography sx={{ mt: 2 }}>
                  <b>Påmelding:</b>
                </Typography>
                <ul style={{ textAlign: 'left', marginTop: 4, marginBottom: 8 }}>
                  <li>
                    Påmelding for konkurranse og trim med tidtaking skjer via{' '}
                    <Button
                      component="a"
                      href="https://signup.eqtiming.com/?Event=Malvik_IL&lang=norwegian"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="text"
                      color="inherit"
                      sx={{ p: 0, minWidth: 0, fontSize: '1em', textTransform: 'none', textDecoration: 'underline', textUnderlineOffset: 4 }}
                    >
                      EQ Timing
                    </Button>.
                  </li>
                  <li>
                    {t('mo.hikingRegistrationNote')}
                  </li>
                </ul>
                <Typography><b>{t('mo.hikingPaymentInfo')}</b></Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default MO2025Page;
